/**
 * Review Center React Query hooks
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    fetchReviewContainers,
    fetchReviewItems,
    fetchReviewItem,
    resolveReviewItem,
    dismissReviewItem,
    fetchHasPending,
    mergeContainers,
    type ResolveRequest,
    type MergeRequest,
} from "@/data/api/review-center";

// ============================================================================
// Query Keys
// ============================================================================

export const REVIEW_CENTER_KEYS = {
    all: ["review-center"] as const,
    containers: () => [...REVIEW_CENTER_KEYS.all, "containers"] as const,
    items: (filters?: Record<string, unknown>) =>
        [...REVIEW_CENTER_KEYS.all, "items", filters] as const,
    detail: (id: string) => [...REVIEW_CENTER_KEYS.all, "detail", id] as const,
    hasPending: () => [...REVIEW_CENTER_KEYS.all, "has-pending"] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

export function useReviewContainers(params?: { skip?: number; limit?: number; include_resolved?: boolean }) {
    return useQuery({
        queryKey: [...REVIEW_CENTER_KEYS.containers(), params?.include_resolved ?? false],
        queryFn: () => fetchReviewContainers(params),
        refetchInterval: 60_000, // refresh every minute
    });
}

export function useReviewItems(
    params?: {
        status?: string;
        review_type?: string;
        container_id?: string;
        search?: string;
        skip?: number;
        limit?: number;
    },
    queryEnabled = true,
) {
    return useQuery({
        queryKey: REVIEW_CENTER_KEYS.items(params),
        queryFn: () => fetchReviewItems(params),
        enabled: queryEnabled,
    });
}

export function useReviewItem(id: string | null) {
    return useQuery({
        queryKey: REVIEW_CENTER_KEYS.detail(id || ""),
        queryFn: () => fetchReviewItem(id!),
        enabled: !!id,
    });
}

export function useHasPendingReviews() {
    return useQuery({
        queryKey: REVIEW_CENTER_KEYS.hasPending(),
        queryFn: fetchHasPending,
        refetchInterval: 30_000, // poll every 30s for badge
    });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

export function useResolveReviewItem() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: ResolveRequest }) =>
            resolveReviewItem(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: REVIEW_CENTER_KEYS.all });
        },
    });
}

export function useDismissReviewItem() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
            dismissReviewItem(id, reason),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: REVIEW_CENTER_KEYS.all });
        },
    });
}

export function useMergeContainers() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: MergeRequest) => mergeContainers(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: REVIEW_CENTER_KEYS.all });
        },
    });
}
